import { redirect, type RouteObject } from 'react-router'
import type { IamFeature } from '@/features/iam'
import { links } from '@/utils/link'
import {
  createProfilePageLoader,
  createProfileViewLoader,
  createTeamViewLoader,
  createTeamsViewLoader,
} from '@/features/iam/iam-route-loaders'

/**
 * Creates the iam routes that do not require authentication.
 */
export function createPublicRoutes(iam: IamFeature): RouteObject[] {
  return [
    {
      path: '/complete-registration/:token',
      loader: ({ params }) => ({
        vm: iam.acquireCompleteRegistrationViewModel(params.token),
      }),
      lazy: {
        Component: () =>
          import('@/features/iam/auth/CompleteRegistration/CompleteRegistrationContainer').then(
            (r) => r.CompleteRegistrationContainer,
          ),
      },
    },
  ]
}

/**
 * Creates the iam routes that render inside the shell.
 */
export function createRoutes(iam: IamFeature): RouteObject[] {
  return [
    {
      path: '/logout/*',
      loader: async ({ request }) => {
        const redirectTo = new URL(request.url).searchParams.get('redirect_to')
        await iam.waitForAuthResolution(request.signal)
        await iam.authStore.logout(redirectTo)
        return null
      },
      lazy: {
        Component: () =>
          import('@/features/iam/auth/components/Logout').then((r) => r.Logout),
      },
    },
    {
      path: '/teams',
      loader: createTeamsViewLoader(iam),
      lazy: {
        Component: () =>
          import('@/features/iam/teams/Teams/WorkspaceTeamsContainer').then(
            (r) => r.default,
          ),
      },
      children: [
        {
          index: true,
          lazy: {
            Component: () =>
              import('@/features/iam/teams/Teams/WorkspaceTeamsIndexContainer').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: ':teamSlug/*',
          loader: createTeamViewLoader(iam),
          lazy: {
            Component: () =>
              import('@/features/iam/teams/Teams/WorkspaceTeamContainer').then(
                (r) => r.default,
              ),
          },
        },
      ],
    },
    {
      path: '/profile',
      loader: createProfileViewLoader(iam),
      lazy: {
        Component: () =>
          import('@/features/iam/users/Profile/WorkspaceProfileContainer').then(
            (r) => r.default,
          ),
      },
      children: [
        {
          index: true,
          loader: () => redirect(links.profileGeneral()),
          Component: null,
        },
        {
          path: 'payouts/method',
          loader: () => redirect(links.profileBillingPayoutMethod()),
          Component: null,
        },
        {
          path: 'general/*',
          lazy: {
            Component: () =>
              import('@/features/iam/users/Profile/General/GeneralContainer').then(
                (r) => r.default,
              ),
          },
        },
        {
          path: 'billing',
          loader: createProfilePageLoader(
            iam,
            (vm) => vm.shouldShowBilling,
            links.profileGeneral(),
          ),
          lazy: {
            Component: () =>
              import('@/features/iam/users/Profile/Billing/BillingContainer').then(
                (r) => r.default,
              ),
          },
          children: [
            {
              index: true,
              loader: () => redirect(links.profileBillingPaymentMethods()),
              Component: null,
            },
            {
              path: 'payment-methods/*',
              lazy: {
                Component: () =>
                  import('@/features/iam/users/Profile/Billing/BillingPaymentMethodsContainer').then(
                    (r) => r.default,
                  ),
              },
            },
            {
              path: 'payout-method/*',
              loader: createProfilePageLoader(
                iam,
                (vm) => vm.billing.isPayoutsEnabled,
                links.profileBillingPaymentMethods(),
              ),
              lazy: {
                Component: () =>
                  import('@/features/iam/users/Profile/Billing/BillingPayoutMethodContainer').then(
                    (r) => r.default,
                  ),
              },
            },
          ],
        },
        {
          path: 'notifications/*',
          lazy: {
            Component: () =>
              import('@/features/iam/users/Profile/Notifications/NotificationsContainer').then(
                (r) => r.default,
              ),
          },
        },
        // Unknown profile paths keep the profile layout, with an empty page.
        { path: '*', Component: null },
      ],
    },
  ]
}
